import React, { Component } from 'react';
import { injectIntl, defineMessages, IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';

import moment from 'moment';

import { Hidden, Chip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import { logEvent, Event } from '../../lib/analytics';
import { MeetingType, findMeetingValue, MeetingMeta } from '../../lib/api/relay/utils/types';
import { State as ReduxState } from '../../lib/reducers';
import getRouteFor, { Route } from '../../lib/utils/router';
import CopyToClipboard from '../CopyToClipboard';
import { IconEdit, IconTrash, IconMore, IconLayoutWebinar, IconLayoutAudio } from '../IconSet';
import ScheduleMeeting from '../ScheduleMeeting';
import StyledMenu from '../StyledMenu';

import { Meeting } from './View';

import style from './styleDetails';


const messages = defineMessages({
  deleteMeeting: { id: 'deleteMeeting' },
  editMeeting: { id: 'editMeeting' },
  joinButton: { id: 'joinButton' },
  attendees: { id: 'attendees' },
  ownMeeting: { id: 'ownMeeting' },
  assistedMeeting: { id: 'assistedMeeting' },
});

export interface ScheduleEvent {
  users: Array<string | null> | null;
  title: string;
  notes: string;
  dtStart: moment.Moment;
  dtEnd: moment.Moment;
  userId: string;
  organizerId: string;
  meetingId: string;
  domainId: string;
  type: MeetingType;
  meta: MeetingMeta;
}

interface State {
  editMeetingDialog: boolean;
  editMeetingDetails: ScheduleEvent | null;
  anchorEl: HTMLElement | null;
}

interface Props {
  //formatDate: (date: string) => string;
  onDelete: (meeting: Meeting) => void;
  meeting: Meeting;
}

type MappedProps = {
  myUserId: ReduxState['session']['userId'];
  defaultMeetingType: ReduxState['session']['defaultMeetingType'];
}

const mapStateToProps = (state: ReduxState): MappedProps => ({
  myUserId: state.session.userId,
  defaultMeetingType: state.session.defaultMeetingType
});

type PropsType = Props
  & WithStyles<typeof style>
  & RouteComponentProps
  & { intl: IntlShape }
  & MappedProps;

class MeetingDetails extends Component<PropsType, State> {

  constructor(props: PropsType) {
    super(props);

    this.handleJoin = this.handleJoin.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.getMeetingActions = this.getMeetingActions.bind(this);
    this.getAssistantBadge = this.getAssistantBadge.bind(this);
    this.getMeetingType = this.getMeetingType.bind(this);

    this.state = {
      editMeetingDialog: false,
      editMeetingDetails: null,
      anchorEl: null,
    };

  }

  handleJoin() {
    const path = getRouteFor(Route.Meeting, { slug: this.props.meeting.slug });
    this.props.history.push(path);
    logEvent(Event.START_SCHEDULED_MEETING);
  }

  handleEdit() {
    this.openScheduleMeetingDialog(this.props.meeting);
  }

  handleDelete = () => {
    this.props.onDelete(this.props.meeting);
    logEvent(Event.DELETE_MEETING);
  }

  openScheduleMeetingDialog(meeting: Meeting) {
    let notes = "";
    const {
      id,
      title,
      dtStart,
      dtEnd,
      attendees,
      userId,
      organizerId,
      domainId,
      type,
      meta
    } = meeting;

    if (meeting.notes) {
      notes = meeting.notes;
    }

    const users = attendees.map(function(a) {
      return a.email;
    });

    const meetingDetails: ScheduleEvent = {
      users: users,
      title: title,
      notes: notes,
      dtStart: moment(dtStart),
      dtEnd: moment(dtEnd),
      meetingId: id,
      userId: userId,
      organizerId: organizerId,
      domainId: domainId,
      type: type,
      meta: meta
    };

    this.setState({
      editMeetingDialog: true,
      editMeetingDetails: meetingDetails
    });
  }

  onEditMeetingClose = () => {
    this.setState({ editMeetingDialog: false, editMeetingDetails: null });
  }

  getMeetingActions = () => {
    const rawUrl = getRouteFor(Route.Meeting, { slug: this.props.meeting.slug }, '', window.location.origin);
    return (
      <div>
        <Tooltip placement="top" title={this.props.intl.formatMessage(messages.deleteMeeting)}>
          <IconButton
            size="small"
            onClick={this.handleDelete}
          >
            <IconTrash size={24} />
          </IconButton>
        </Tooltip>
        <CopyToClipboard rawUrl={rawUrl} />
        <Tooltip placement="top" title={this.props.intl.formatMessage(messages.editMeeting)}>
          <IconButton
            size="small"
            onClick={this.handleEdit}
          >
            <IconEdit size={24} />
          </IconButton>
        </Tooltip>
        <Button color="primary" onClick={this.handleJoin} className={this.props.classes.buttonNoStyle}>
          {this.props.intl.formatMessage(messages.joinButton)}
        </Button>
      </div>
    );
  };

  getAssistantBadge = () => {
    const { myUserId, meeting: { userId } } = this.props;
    const { formatMessage } = this.props.intl;

    return (
      <React.Fragment>
        {(myUserId !== userId) &&
          <Chip
            label={formatMessage(messages.assistedMeeting)}
            size='small'
            color='secondary'
          />
        }
      </React.Fragment>
    );
  };

  handleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    this.setState({ anchorEl: event.currentTarget });
  };

  closeMenu = (): void => {
    this.setState({ anchorEl: null });
  }

  getMeetingTypeIcon = (type: MeetingType) => {
    const size = 24;
    switch (type) {
      case 'AUDIO_ONLY': return <IconLayoutAudio size={size} />;
      case 'LESSON': return <IconLayoutWebinar size={size} />;
      case 'WEBINAR': return <IconLayoutWebinar size={size} />;
    }
  }

  getMeetingType = () => {
    const { classes, defaultMeetingType, meeting: { type }, intl: { formatMessage } } = this.props;
    const meetingValue = findMeetingValue(type);
    return (
      <React.Fragment>
        {(defaultMeetingType !== 'REGULAR' || defaultMeetingType !== type)
          ?
          <React.Fragment>
            <Typography variant="caption" className={classes.dtContainer}>
              |
            </Typography>
            <Tooltip placement='top' title={formatMessage(meetingValue.label)}>
              <div>
                {this.getMeetingTypeIcon(meetingValue.value)}
              </div>
            </Tooltip>
          </React.Fragment>
          : null
        }
      </React.Fragment>
    );
  }

  render() {
    const { classes, intl } = this.props;
    const { title, dtStart, dtEnd, attendees } = this.props.meeting;
    const open = Boolean(this.state.anchorEl);
    const start = moment(dtStart).format('HH:mm');
    const end = moment(dtEnd).format('HH:mm');
    return (
      <div className={classes.container}>

        <Grid container spacing={3} justify-content="space-around">
          <Grid item xs={10} md={8} className={classes.detailsInfo}>
            <Grid container className={classes.titleContainer}>
              <Typography variant="body2" className={classes.title}>
                {title}
              </Typography>
              {this.getAssistantBadge()}
            </Grid>
            <Grid container alignItems='center'>
              <Typography variant="caption" className={classes.dtContainer}>
                {start} - {end}
              </Typography>
              <Typography variant="caption" className={classes.dtContainer}>
                |
              </Typography>
              <Typography variant="caption" className={classes.dtContainer}>
                {attendees ? attendees.length : 0} {intl.formatMessage(messages.attendees).toLowerCase()}
              </Typography>
              {this.getMeetingType()}
            </Grid>
          </Grid>
          <Hidden smDown>
            <Grid item md={4} className={classes.actionButtons}>
              {this.getMeetingActions()}
            </Grid>
          </Hidden>
          <Hidden mdUp>
            <Grid item xs={2} className={classes.actionButtons}>
              <IconButton onClick={this.handleMenu}>
                <IconMore size={20} />
              </IconButton>
              <StyledMenu
                anchorEl={this.state.anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={this.closeMenu}
              >
                {this.getMeetingActions()}
              </StyledMenu>
            </Grid>
          </Hidden>
        </Grid>
        {
          this.state.editMeetingDialog === true &&
          <ScheduleMeeting
            onCloseClick={this.onEditMeetingClose}
            meetingDetails={this.state.editMeetingDetails}
            dialogTitle={intl.formatMessage(messages.editMeeting)}
          />
        }
      </div>
    );
  }
}


export default withStyles(style)(injectIntl(connect(mapStateToProps)(withRouter(MeetingDetails))));
